import React, { useState, useContext, useEffect, useCallback } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, Navigate } from "react-router-dom";
import ProfileLoading from "./ProfileLoading";
import { v4GridContainerWidthSx } from "../../theme/v4CompatSx";
import { Form } from "react-final-form";
import { useToasts } from "react-toast-notifications";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import ReactCodeInput from "react-verification-code-input";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** The mobile number is a login identifier, and the
 * change is gated on an SMS verification code plus a lifetime change limit
 * (`phoneNumberChanges <= 5`). The conversion is styling-only: no handler,
 * validator, state value, prop, condition or DOM node moved — the change-limit
 * branch and the `phoneNumberChanges + 1` write are carried across verbatim.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer` and `myAccountTitle` were declared but never
 * referenced from the JSX (copy-paste from MyAccount.js), so they rendered
 * nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().verificationCodeContainer — `react-verification-code-input`
// is a third-party component that only accepts a `className` (no `sx` prop),
// so this is `styled()` rather than an `sx` constant (guide §1.2).
const VerificationCodeInput = styled(ReactCodeInput)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  width: "100% !important",
  fontWeight: "600",
  "& input": {
    height: "56px !important",
    width: "15.6% !important",
    borderTop: "none",
    borderRight: "none",
    borderLeft: "none",
    borderTopLeftRadius: "0px !important",
    borderBottomLeftRadius: "0px !important",
    borderTopRightRadius: "0px !important",
    borderBottomRightRadius: "0px !important",
    borderBottom: 0,
    border: "1px solid #a5a5a5 !important",
    backgroundColor: "#f7f7f7 !important",
    margin: "0 0.5%",
    fontSize: "2rem",
    fontWeight: 500,
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    "&:focus": {
      border: "1px solid green",
      caretColor: "green",
    },
    "&:last-child": {
      borderRight: "none",
    },
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim rather than re-deriving the
 * arithmetic: omit `spacing` (v9 default 0 ⇒ `gap: 0px`), and give each item
 * the 8px padding itself. Identical by construction.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
    fontSize: "1.3rem",
  },
});

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().formContainer
//
// PARITY RESTORE (guide §2, "`<Grid container>` lost its `width: 100%`"): this
// container's direct parent is another `<Grid container>`, so in v9 — where the
// Grid container emits no width rule at all — it becomes a flex item at
// `width: auto` and shrinks to fit its content. Measured on the change-email
// form: 718.66px → 245.41px.
const formContainerSx = (theme) => ({
  ...v4GridContainerWidthSx,
  // marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
});

// was: useStyles().inputField, merged with the Grid item padding above. Every
// selector here targets `react-phone-input-2`'s own class names, not `.Mui*`
// ones, so the guide §1.2b class-prefix trap does not apply.
const inputFieldItemSx = (theme) => ({
  padding: "8px",
  "& .react-tel-input": {
    marginTop: 0,
    marginBottom: theme.spacing(1),
  },
  "& .react-tel-input .flag-dropdown:before": {
    display: "none !important",
  },
  "& .react-tel-input .form-control": {
    border: "1px solid #a5a5a5 !important",
    backgroundColor: "#f7f7f7 !important",
    fontSize: "16px !important",
    borderRadius: "5px !important",
    width: "100% !important",
  },
});

// was: useStyles().footerDivider
const footerDividerSx = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "5% 0 2% 0",
};

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default, which would paint the label lime on the lime
// `typography.button` background — an invisible button. "Resend Code" is
// `variant="outlined"` and needs the same restore (themeV5's `outlined` slot
// only restates `color` on `:hover`).
const submitSx = (theme) => ({
  margin: "10px 0",
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginTop: 0,
    marginRight: theme.spacing(1.5),
    width: "auto",
  },
  color: "text.primary",
});

// The "My Account" button on the change-limit-exceeded branch carries no v4
// rule of its own — only the same removed default colour to restore.
const myAccountButtonSx = { color: "text.primary" };

// was: useStyles().validatonError (v4 spelling kept — it is only a key name)
const validationErrorSx = (theme) => ({ marginBottom: theme.spacing(2) });

/**
 * #773 — the profile gate, plus the "no number known" route.
 *
 * Three things happen here, in a deliberate order.
 *
 * 1. `AccountContext` initialises `userProfile` as `useState<UserProfile>()`
 *    (AccountContext.tsx:170), so it is undefined on the first render of every
 *    cold navigation. This screen did not crash on it (its `useState`s already
 *    use `?.`), but it rendered NOTHING while the profile loaded, and — worse —
 *    `useState` captures its initialiser once, so `phoneNumber`/`countryCode`
 *    stayed `undefined` for the life of the component even after the profile
 *    arrived. Mounting the form only once a profile exists fixes both.
 *
 * 2. THE CHANGE LIMIT IS CHECKED BEFORE THE REDIRECT, and this ordering is
 *    load-bearing. `phoneNumberChanges <= 5` is not a UX nicety: it exists to
 *    stop a member freeing up their number over and over so the old number can
 *    be reused for another free trial. Sending a locked-out member to
 *    `/myaccount/add-phone` — which has no such gate — would have turned this
 *    screen into the way around it. So a member over the limit sees the
 *    existing "you have exceeded the number of times" message whether or not
 *    they currently have a number on file. That is new only for the
 *    no-number-and-locked-out case, which previously rendered a blank page.
 *
 * 3. Otherwise, a member with no number known gets the ADD PHONE experience
 *    rather than an empty screen (operator ruling, 2026-07-31: a dial code with
 *    no number is the same as no number at all). A redirect rather than an
 *    inline render, so the URL, breadcrumb and heading all agree with what the
 *    member is actually doing; `replace` so Back does not bounce them between
 *    the two screens.
 *
 * NOT a hole this opens: `/myaccount/add-phone` is already an unguarded route
 * anyone can type, and the API increments the change counter on any profile
 * update (ProfileController.ts:224), so the redirect neither creates a new
 * capability nor skips the count. The limit itself is only enforced in this
 * component, though — see the note filed against the API.
 */
export default function ChangePhone() {
  const { userProfile } = useContext(AccountContext);

  if (!userProfile) {
    return <ProfileLoading />;
  }

  // Same expression as the ternary inside the form, deliberately — so the
  // lock-out truth table is unchanged for members who do have a number.
  const withinChangeLimit = userProfile.phoneNumberChanges <= 5;
  const hasNumber = !!(userProfile.countryCode && userProfile.phoneNumber);

  if (withinChangeLimit && !hasNumber) {
    return <Navigate to="/myaccount/add-phone" replace />;
  }

  return <ChangePhoneForm />;
}

function ChangePhoneForm() {
  const { addToast } = useToasts();

  // Account Context
  const {
    userProfile,
    update,
    updating,
    updateError,
    getVerificationCode,
    waitingOnVerificationCode,
    validateLogin,
    resetLoginWithPhone,
    loading,
  } = useContext(AccountContext);

  // State
  const [verificationCode, setVerificationCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(userProfile?.phoneNumber);
  const [countryCode, setCountryCode] = useState(userProfile?.countryCode);
  const onChange = (value, { dialCode }) => {
    setPhoneNumber(value.substring(dialCode.length));
    setCountryCode(dialCode);
  };

  useEffect(() => {
    resetLoginWithPhone();
    // eslint-disable-next-line
  }, []);

  const onSubmitMobile = useCallback(async () => {
    if (!countryCode || !phoneNumber) {
      addToast("You must enter a new mobile number", {
        appearance: "error",
      });
      return;
    }

    if (
      userProfile.countryCode === countryCode &&
      userProfile.phoneNumber === phoneNumber
    ) {
      addToast(
        "You're new phone number must be different from you current number!",
        {
          appearance: "error",
        },
      );
      return;
    }

    try {
      await getVerificationCode({ countryCode, phoneNumber });
    } catch (e) {
      addToast("Oops! There was an error sending the verification code", {
        appearance: "error",
      });
    }
  }, [addToast, getVerificationCode, phoneNumber, countryCode, userProfile]);

  // Submit Verification Code
  const onSubmitCode = useCallback(async () => {
    try {
      await update({
        countryCode: countryCode,
        phoneNumber: phoneNumber,
        phoneVerificationCode: verificationCode,
        phoneNumberChanges: userProfile.phoneNumberChanges + 1,
      });
      validateLogin();
      addToast("Phone updated successfully!", {
        appearance: "success",
      });
    } catch (e) {
      addToast("Oops! There was an problem updating your phone", {
        appearance: "error",
      });
    }
  }, [
    validateLogin,
    addToast,
    update,
    phoneNumber,
    countryCode,
    verificationCode,
    userProfile,
  ]);

  return (
    <>
      {/* Was `userProfile && countryCode && phoneNumber`. The gate above now
          guarantees a profile, and guarantees that anyone reaching here either
          HAS a number or is over the change limit — so requiring a number here
          as well is what made the no-number-and-locked-out member fall through
          to a blank page. Dropping it sends them to the ternary's lock-out
          branch, which is the message they should see. Members with a number
          are unaffected: both branches below behave exactly as before. */}
      {userProfile && (
        <>
          {userProfile.phoneNumberChanges <= 5 ? (
            <>
              <Grid container>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={breadcrumbContainerSx}
                  >
                    <ActiveBreadcrumbLink to="/myaccount/overview">
                      Overview
                    </ActiveBreadcrumbLink>
                    <Typography sx={disabledBreadcrumbSx}>
                      Change Phone
                    </Typography>
                  </Breadcrumbs>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography variant="h2" sx={myAccountSubTitleSx}>
                    Change Phone
                  </Typography>
                </Grid>
                <Grid container sx={formContainerSx}>
                  {!updating && (
                    <>
                      <Grid size={{ xs: 12, sm: 8, md: 6 }}>
                        {!waitingOnVerificationCode && (
                          <>
                            {loading && (
                              <Box sx={{ m: 5 }} align="center">
                                <CircularProgress variant="indeterminate"></CircularProgress>
                                <Typography variant="body2" sx={bodyTextSmlSx}>
                                  <br />
                                  Sending verification code...
                                </Typography>
                              </Box>
                            )}
                            {!loading && (
                              <>
                                {userProfile &&
                                  userProfile.countryCode &&
                                  userProfile.phoneNumber && (
                                    <>
                                      <Typography
                                        variant="body1"
                                        sx={bodyTextSx}
                                        style={{ marginBottom: "24px" }}
                                      >
                                        Enter your new mobile phone number
                                        below.
                                      </Typography>
                                      <Form
                                        onSubmit={onSubmitMobile}
                                        initialValues={{
                                          countryCode: userProfile.countryCode,
                                          phoneNumber: userProfile.phoneNumber,
                                        }}
                                      >
                                        {(props) => (
                                          <form onSubmit={props.handleSubmit}>
                                            <Grid
                                              container
                                              sx={v4GridContainerSx}
                                            >
                                              <Grid
                                                size={{ xs: 12, sm: 12 }}
                                                sx={inputFieldItemSx}
                                              >
                                                {updateError && (
                                                  <Alert
                                                    severity="error"
                                                    variant="filled"
                                                    style={{
                                                      marginBottom: "12px",
                                                    }}
                                                  >
                                                    {updateError}
                                                  </Alert>
                                                )}
                                                <PhoneInput
                                                  inputProps={{
                                                    name: "mobileNumber",
                                                    required: true,
                                                    autoFocus: false,
                                                  }}
                                                  placeholder="Mobile Number"
                                                  aria-label="Mobile Number"
                                                  preferredCountries={[
                                                    "gb",
                                                    "ie",
                                                    "us",
                                                    "au",
                                                    "nz",
                                                  ]}
                                                  excludeCountries={["je"]}
                                                  onChange={onChange}
                                                  enableTerritories={true}
                                                  disabled={updating}
                                                  countryCodeEditable={false}
                                                  value={
                                                    userProfile.countryCode +
                                                    userProfile.phoneNumber
                                                  }
                                                />
                                              </Grid>
                                              <Grid
                                                size={{ xs: 12, sm: 12 }}
                                                sx={v4GridItemSx}
                                              >
                                                <Button
                                                  sx={submitSx}
                                                  type="submit"
                                                  disabled={updating}
                                                >
                                                  Update Mobile
                                                </Button>
                                              </Grid>
                                            </Grid>
                                          </form>
                                        )}
                                      </Form>
                                    </>
                                  )}
                                {!userProfile.countryCode &&
                                  !userProfile.phoneNumber && (
                                    <Typography
                                      variant="body1"
                                      sx={bodyTextSx}
                                      style={{ marginBottom: "24px" }}
                                    >
                                      You need to add a phone.
                                    </Typography>
                                  )}
                              </>
                            )}
                          </>
                        )}
                        {waitingOnVerificationCode && (
                          <>
                            <Typography
                              variant="body1"
                              sx={bodyTextSx}
                              align="left"
                              style={{ marginBottom: "24px" }}
                            >
                              We need to know that it&apos;s really you so
                              we&apos;ve sent you a verification code.
                              <br />
                              <br />
                              Please enter the code sent to:
                              <br />
                              <br />
                              <span>
                                +{countryCode}
                                {phoneNumber}
                              </span>
                            </Typography>
                            <Grid size={{ xs: 12, sm: 12 }}>
                              {updateError && (
                                <Alert
                                  severity="error"
                                  variant="filled"
                                  sx={validationErrorSx}
                                >
                                  {updateError}
                                </Alert>
                              )}
                            </Grid>
                            <>
                              <Grid size={{ xs: 12, sm: 12 }}>
                                <VerificationCodeInput
                                  type="number"
                                  fields={6}
                                  value={verificationCode}
                                  onComplete={(e) => setVerificationCode(e)}
                                  disabled={updating}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 12 }}>
                                <Button
                                  size="medium"
                                  sx={submitSx}
                                  onClick={onSubmitCode}
                                >
                                  Verify Phone
                                </Button>

                                <Button
                                  size="medium"
                                  variant="outlined"
                                  sx={submitSx}
                                  onClick={onSubmitMobile}
                                >
                                  Resend Code
                                </Button>
                              </Grid>
                            </>
                          </>
                        )}
                        <Divider sx={footerDividerSx} />
                        <Typography variant="body1" sx={bodyTextSmlSx}>
                          <span>Tip</span> - Did you know you can log in with
                          your Mobile Number?
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 5 }}></Grid>
                    </>
                  )}
                  {updating && (
                    <Box sx={{ m: 5 }} align="center">
                      <CircularProgress variant="indeterminate"></CircularProgress>
                      <Typography variant="body2" sx={bodyTextSmlSx}>
                        <br />
                        Updating your Phone Number. Please do not refresh your
                        browser.
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid container>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="h2" sx={myAccountSubTitleSx}>
                    Change Phone
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={bodyTextSx}
                    style={{ marginBottom: "24px" }}
                  >
                    You have exceeded the number of times you can change your
                    phone number. Please contact support or email{" "}
                    <a
                      style={{ color: "#171717", fontWeight: "600" }}
                      href="mailto:info@farmflix.tv"
                    >
                      info@farmflix.tv
                    </a>{" "}
                    for help.
                  </Typography>
                  <Link
                    to="/myaccount/overview"
                    style={{ textDecoration: "none" }}
                  >
                    <Button size="medium" sx={myAccountButtonSx}>
                      My Account
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
}
